import {
	addDays,
	getIsToday,
	getStartOfDay,
	isBetween,
	parseDtoDate
} from "client/DateAndTime/dateTimeUtils";
import { TimeZone } from "client/DateAndTime/timezones";
import {
	convertToDateOnly,
	convertToDateTime,
	parseDtoDateOnlyIgnoringOffset
} from "client/DateAndTime/dateUtils";
import { DateOnly } from "client/DateAndTime/dateOnly";
import { AppointmentViewModel } from "client/Api/Calendar/calendar";
import { ensureNotNil } from "client/Misc/utils";

interface AppointmentSpanInfo {
	startDateToCompare: Date;
	endDateToCompare: Date;
}

interface AppointmentSpanFlags{
	startOrEndsOnDay: boolean;
	startsBeforeDay: boolean;
	endsAfterDay: boolean;
	isSingleDay: boolean;
	isToday: boolean;
}

type AppointmentsViewModelWithSpanFlags = AppointmentViewModel & AppointmentSpanFlags;

export type AppointmentSpanInput = {
	start: string | Date;
	end: string | Date;
	isAllDayEvent: boolean;
};

class CalendarLogic {
	public getAppointmentSpanInfo(
		appointment: AppointmentSpanInput,
		timezone: TimeZone
	): AppointmentSpanInfo {
		const resolveDate = (value: Date | string, isAllDayEvent: boolean): Date => {
			if (value instanceof Date) {
				return value;
			}

			if (!isAllDayEvent) {
				return parseDtoDate(value);
			}

			// We want to render all day events for the date they were created no matter timezone
			// for example if all day event was created on 2023-08-09 in Amsterdam
			// it should be rendered on this date in any other timezone
			return convertToDateTime(
				ensureNotNil(
					parseDtoDateOnlyIgnoringOffset(value),
				),
				timezone
			);
		};

		const startDateToCompare: Date = resolveDate(appointment.start, appointment.isAllDayEvent);
		const endDateToCompare: Date = resolveDate(appointment.end, appointment.isAllDayEvent);

		return {
			endDateToCompare,
			startDateToCompare
		};
	}

	public getAppointmentSpanFlags(
		appointment: AppointmentSpanInput,
		date: Date,
		timezone: TimeZone
	): AppointmentSpanFlags {
		const startOfDay: Date = getStartOfDay(date, timezone);
		const startOfNextDay: Date = addDays(startOfDay, 1, timezone);

		const {
			startDateToCompare,
			endDateToCompare
		} = this.getAppointmentSpanInfo(appointment, timezone);

		const startsOnTheDay: boolean = isBetween(
			startDateToCompare,
			startOfDay,
			startOfNextDay
		);

		const endsOnTheDay: boolean = isBetween(
			endDateToCompare,
			startOfDay,
			startOfNextDay,
			{
				shouldIncludeStart: false,
				shouldIncludeEnd: true
			}
		);

		const startOrEndsOnDay: boolean = startsOnTheDay || endsOnTheDay;
		const startsBeforeDay: boolean = startDateToCompare.getTime() < startOfDay.getTime();
		const endsAfterDay: boolean = endDateToCompare.getTime() > startOfNextDay.getTime();

		const startDate: DateOnly = convertToDateOnly(
			startDateToCompare,
			timezone
		);

		const endDate: DateOnly = convertToDateOnly(
			new Date(endDateToCompare.getTime() - 1),
			timezone
		);

		const isSingleDay: boolean = startDate.isEqualTo(endDate);

		const isToday: boolean = getIsToday(startDateToCompare, timezone);

		return {
			startOrEndsOnDay,
			endsAfterDay,
			startsBeforeDay,
			isSingleDay,
			isToday
		};
	}

	public getAppointmentsPerDay<TModel extends AppointmentViewModel>(
		allAppointments: readonly TModel[],
		day: Date,
		timezone: TimeZone
	): (TModel & AppointmentSpanFlags)[] {

		return allAppointments.map(appointment => ({
			...appointment,
			...this.getAppointmentSpanFlags(appointment, day, timezone)
		}))
			.filter(appointment => appointment.startOrEndsOnDay || (appointment.startsBeforeDay && appointment.endsAfterDay));
	}

	public checkIfDayHasAppointments(
		allAppointments: readonly AppointmentViewModel[],
		day: Date,
		timezone: TimeZone
	) : boolean {
			return this.getAppointmentsPerDay(
				allAppointments,
				day,
				timezone
			)
				.length > 0;
		}
}

const calendarLogic = new CalendarLogic();

export {
	calendarLogic,
	CalendarLogic,
	type AppointmentSpanInfo,
	type AppointmentSpanFlags,
	type AppointmentsViewModelWithSpanFlags
};